import { FunctionComponent, useState, useEffect, useRef } from "react";

// Interfaces
import { I_ApprovalStatusCounts } from "src/components/approvals/interfaces";

// Services / Enums
import VIS from "../../VehicleIncidentsService";
import approvalService from "src/components/approvals/ApprovalService";
import { LoadState } from "../../enums";

// Constants
import vehicleIncidentsStatusAliases from "../../constants/VehicleIncidentsStatusAliases";
import { getPendingStatusTypeId } from "../../constants/VehicleIncidentsApprovalConfig";

// Styles
import "./VehicleIncidentsSwitchView.css";

interface Props {
  statusCounts: I_ApprovalStatusCounts[];
}

/**
 * Renders clickable status "tabs".  A tab turns orange (CSS class
 * `VehicleIncidentsSwitchViewItemPending`) when its
 * `ApprovalStatusTypeId === 1` so reviewers can spot items waiting for them.
 */
const VehicleIncidentsSwitchView: FunctionComponent<Props> = ({ statusCounts }) => {

  const { refresh } = VIS;
  const { getAlias } = approvalService

  const [checked, setChecked] = useState(0); // currently‑selected tab index
  const [changedIndexes, setChangedIndexes] = useState<number[]>([]); // fades
  const previousCounts = useRef<I_ApprovalStatusCounts[]>([]);

  // Highlight any count that changed since last render
  useEffect(() => {

    // Assign setter function to VIS
    Object.assign(VIS, {
      setChecked
    })

    const changed: number[] = [];
    statusCounts.forEach((item, idx) => {
      if (previousCounts.current[idx]?.Total !== item.Total) changed.push(idx);
    });

    if (changed.length) {
      setChangedIndexes(changed);
      const timer = setTimeout(() => setChangedIndexes([]), 500);
      return () => clearTimeout(timer);
    }

    previousCounts.current = statusCounts;
  }, [statusCounts]);

  return (
    <div className="VehicleIncidentsSwitchView">
      {statusCounts.map((item, i) => {
        const isChecked = checked === i;
        const isChanged = changedIndexes.includes(i);
        const isPending = item.ApprovalStatusTypeId === getPendingStatusTypeId(approvalService.reference);

        const classes = [
          "VehicleIncidentsSwitchViewItem",
          isChecked
            ? "VehicleIncidentsSwitchViewItemChecked"
            : "VehicleIncidentsSwitchViewItemUnchecked",
          isChanged && "VehicleIncidentsSwitchViewItemChanged",
          isPending && "VehicleIncidentsSwitchViewItemPending",
        ]
          .filter(Boolean)
          .join(" ");

        return (
          <div
            key={i}
            className={classes}
            onClick={() => {
              setChecked(i);
              refresh(item.ApprovalStatusTypeId ?? -999, LoadState.SwitchingViews);
            }}
          >
            <div className="VehicleIncidentsSwitchViewItemStatus">{getAlias(vehicleIncidentsStatusAliases, item)}</div>
            <div className="VehicleIncidentsSwitchViewItemTotal VehicleIncidentsSwitchViewItemTotalFadeWrapper">
              <span
                key={item.Total}
                className="VehicleIncidentsSwitchViewItemTotalFadeWrapperFadeIn"
              >
                {item.Total}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default VehicleIncidentsSwitchView;